import { Box, Typography } from "@mui/material";
import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  Navigate,
  RouterProvider,
  createBrowserRouter,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { Sessions } from "./api/account/Sessions";
import "./App.css";
import Error404 from "./components/Error404";
import { useAuth } from "./hooks/useAuth";
import AutoRebalanceNotifier from "./pages/AutoRebalanceNotifier/AutoRebalanceNotifier";
import DailyReport from "./pages/DailyReport/DailyReport";
import Home from "./pages/Home";
import Login from "./pages/Login/Login";
import LogoManager from "./pages/LogoManager/LogoManager";
import PdfPublisher from "./pages/PdfPublisher/PdfPublisher";
// import PortfoliosPermissions from "./pages/PortfoliosPermissions/PortfoliosPermissions";
import Publications from "./pages/Publications/Publications";
import SystematicPortfolios from "./pages/systematic/SystematicPortfolios";
import Tags from "./pages/Tags/Tags";
import UserManager from "./pages/UserManager/UserManager";
import ChatDocs from "./pages/ChatDocs/ChatDocs";
import {
  ProvideFeedbackSystem,
  useFeedbackSystem,
} from "./hooks/useFeedbackSystem";
import Automation from "./pages/Automation/Automation";
import PortfoliosUsersManagement from "./pages/PortfoliosUsersManagement/PortfoliosUsersManagement";

export const storageContext = createContext<any>(null);
export const storageDispatchContext = createContext(null);

// const BASE_NAME = (process.env.REACT_APP_BASENAME || "/").trim();
export type TabURI =
  | "systematic"
  | "publications"
  | "tags"
  | "dailyReport"
  | "pdfPublisher"
  | "userManager"
  | "logoManager"
  | "portfoliosPermissions"
  | "portfoliosUsers"
  | "autoRebalanceNotifier"
  | "chatDocs"
  | "orchestration";

type PermissionsType = "MASTER" | "ORCHESTRATOR";
export const validPermissions: PermissionsType[] = ["MASTER", "ORCHESTRATOR"];
export const TABS: { title: string; uri: TabURI; component: any }[] = [
  {
    title: "Systematic portfolios",
    uri: "systematic",
    component: <SystematicPortfolios />,
  },
  {
    title: "Rebalance Notifier ",
    uri: "autoRebalanceNotifier",
    component: <AutoRebalanceNotifier />,
  },
  {
    title: "Publications",
    uri: "publications",
    component: <Publications />,
  },
  { title: "Tag management", uri: "tags", component: <Tags /> },
  {
    title: "Daily report",
    uri: "dailyReport",
    component: <DailyReport />,
  },
  {
    title: "S3 Management",
    uri: "pdfPublisher",
    component: <PdfPublisher />,
  },
  {
    title: "Users Management",
    uri: "userManager",
    component: <UserManager />,
  },
  {
    title: "Logo Manager",
    uri: "logoManager",
    component: <LogoManager />,
  },
  // {
  //   title: "portfolios.com",
  //   uri: "portfoliosPermissions",
  //   component: <PortfoliosPermissions />,
  // },
  {
    title: "Portfolios Users",
    uri: "portfoliosUsers",
    component: <PortfoliosUsersManagement />,
  },
  {
    title: "Chat Docs",
    uri: "chatDocs",
    component: <ChatDocs />,
  },
  {
    title: "Orchestration",
    uri: "orchestration",
    component: <Automation />,
  },
];
interface PermissionConfigs {
  tabs: TabURI[];
  index: TabURI;
}
export const permissionsAndTabs: Record<PermissionsType, PermissionConfigs> = {
  MASTER: {
    tabs: [
      "systematic",
      "publications",
      "tags",
      "dailyReport",
      "pdfPublisher",
      "userManager",
      "logoManager",
      "portfoliosPermissions",
      "portfoliosUsers",
      "autoRebalanceNotifier",
      "chatDocs",
    ],
    index: "dailyReport",
  },
  ORCHESTRATOR: {
    tabs: ["orchestration"],
    index: "orchestration",
  }
};

function App() {
  const [routes, setRoutes] = useState(() => {
    const _routes: any[] = [
      {
        path: "/login",
        element: <Login />,
      },
      {
        path: "*",
        element: <Error404 />,
      },
    ];

    const appChildren: any[] = TABS.map((tab) => ({
      path: tab.uri,
      element: <ProtectedTab>{tab.component}</ProtectedTab>,
    }));

    // initially the default tab is not set
    // appChildren.push({
    //   index: true, // route di default, se uno va su "/"
    //   element: <Navigate to="dailyReport" />,
    // });

    _routes.push({
      path: "/",
      element: (
        <ProtectedRoutes>
          <Home />
        </ProtectedRoutes>
      ),
      children: appChildren,
    });
    return _routes;
  });

  const auth = useAuth();
  useEffect(() => {
    // initially the default tab is not set so when the user logs in we have to findout what's his default tab
    if (auth.isLogged) {
      let permissions: any[] = auth.user?.permissions ?? [];
      permissions = permissions.filter((permission) =>
        validPermissions.includes(permission),
      );
      const defaultTab = permissionsAndTabs[permissions[0]].index;
      const _routes: any[] = [
        {
          path: "/login",
          element: <Login />,
        },
        {
          path: "*",
          element: <Error404 />,
        },
      ];

      const appChildren: any[] = TABS.map((tab) => ({
        path: tab.uri,
        element: <ProtectedTab>{tab.component}</ProtectedTab>,
      }));

      appChildren.push({
        index: true, // route di default, se uno va su "/"
        element: <Navigate to={defaultTab} />,
      });

      _routes.push({
        path: "/",
        element: (
          <ProtectedRoutes>
            <Home />
          </ProtectedRoutes>
        ),
        children: appChildren,
      });
      setRoutes(_routes);
    }
  }, [auth.isLogged, auth.user?.permissions]);
  const router = createBrowserRouter(routes, {basename:"/"});
  return (
    <ProvideFeedbackSystem>
      <div className="App">
        <RouterProvider router={router} />
      </div>
    </ProvideFeedbackSystem>
  );
}

export default App;

//#region ProtectedRoutes - protect router from unauthenticated users
const ProtectedRoutes = ({ children }) => {
  const navigate = useNavigate();
  const feedback = useFeedbackSystem();
  const { initOnAuth, isLogged, isAllowedToAccessSupport } = useAuth();
  const location = useLocation();
  const checkAuth = useCallback(async () => {
    if (!isLogged) {
      const sessionAPI = new Sessions();
      try {
        console.log(document.cookie);
        const response: any = await sessionAPI.isValid();
        const isAllowed: boolean = await isAllowedToAccessSupport(response);
        if (!isAllowed) {
          feedback.show({
            type: "warning",
            message: `${response.email}(${response.firstName} ${response.lastName}) not authorized to access Support`,
          });
        }
        let _isAuth = false;
        if (
          response != null &&
          response.id != null &&
          response["permissions"].length > 0
        ) {
          _isAuth = true;
        }
        if (_isAuth) {
          await initOnAuth(response);
        }
      } catch (error) {
        navigate("/login", { state: { from: location } });
      }
    }
  }, [
    feedback,
    initOnAuth,
    isAllowedToAccessSupport,
    isLogged,
    location,
    navigate,
  ]);
  useEffect(() => {
    checkAuth();
  }, [checkAuth]);
  return isLogged ? children : null;
};
//#endregion

//#region - ProtectedTab - protecttab based on users permissions
const ProtectedTab = ({ children }: any) => {
  const auth = useAuth();
  const location = useLocation();
  const actualTab = useMemo(
    () => location.pathname.slice(1),
    [location.pathname],
  );

  const isTabAllowedForUser = useMemo(() => {
    let isAllowed = false;
    if (auth.isLogged) {
      let permissions: any[] = auth.user?.permissions ?? [];
      permissions = permissions.filter((permission) =>
        validPermissions.includes(permission),
      );
      const availableURIsForThesePermissions: any = new Set<string>();
      if (permissions && permissions.length > 0) {
        for (const permission of permissions) {
          if (
            permissionsAndTabs[permission] &&
            permissionsAndTabs[permission].tabs.length > 0
          ) {
            permissionsAndTabs[permission].tabs.forEach((element: TabURI) => {
              availableURIsForThesePermissions.add(element);
            });
          }
        }
      }
      let uris = Array.from(availableURIsForThesePermissions);
      if (uris.includes(actualTab)) {
        isAllowed = true;
      }
    }
    return isAllowed;
  }, [actualTab, auth.isLogged, auth.user?.permissions]);
  return isTabAllowedForUser ? (
    children
  ) : (
    <Box
      width={"100%"}
      height={"100%"}
      display={"flex"}
      justifyContent={"center"}
      alignItems={"center"}
    >
      <Typography>
        {auth?.user?.firstName} {auth?.user?.lastName}, you have no{" "}
        <strong>permissions</strong> to access this functionality!
      </Typography>
    </Box>
  );
};
//#endregion
